import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout/Layout';
import { LogoBox } from '../components/common/LogoBox';
import Timer from '../components/common/Timer';
// import LanguageSelector from '../components/LanguageSelector'; // UI 제거 (기능은 유지)
import HomeButton from '../components/common/HomeButton';
import { useNavigation } from '../hooks/useNavigation';
import { RootState } from '../store/rootReducer';
import { useTimeoutHandler } from '../hooks/useTimeoutHandler';
import { useAiImageAnalytics } from '../hooks/useAiImageAnalytics';
import { usePagination } from '../hooks/usePagination';
import { setSelectedThemeId } from '../store/themeSlice';
import { resetPhotoState } from '../store/photoSlice';
import { ArrowLeftIcon, ArrowRightIcon } from '../components/icons/Icons';
import {
  Container,
  DashedBox,
  ThemeGridContainer,
  ThemeItem,
  PaginationButton,
  PageStatus,
  ThemeConfirmModal,
  ModalContent,
  ModalPreviewImage,
  ModalButtonsContainer,
  ModalButton,
} from './ThemePage.styled';

const THEMES_PER_PAGE = 6;

export function ThemePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedThemeIdFromStore = useSelector((state: RootState) => state.theme.selectedThemeId);
  const block = useSelector((state: RootState) => state.block.block);
  const { navigateToCopy } = useNavigation();
  const { handleThemeTimeout } = useTimeoutHandler();
  const { recordEntryData } = useAiImageAnalytics();
  const [selectedThemeId, setSelectedThemeIdState] = useState<number | null>(
    selectedThemeIdFromStore
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedThemePreviewImage, setSelectedThemePreviewImage] = useState<string | null>(null);

  const themes = block?.themes || [];

  const { currentPage, totalPages, goToNextPage, goToPrevPage } = usePagination({
    totalItems: themes.length,
    itemsPerPage: THEMES_PER_PAGE,
  });

  useEffect(() => {
    dispatch(resetPhotoState());
    recordEntryData();
  }, [dispatch, recordEntryData]);

  const currentThemes = themes.slice(
    (currentPage - 1) * THEMES_PER_PAGE,
    currentPage * THEMES_PER_PAGE
  );

  const handleThemeSelect = (id: number, image: string | undefined) => {
    setSelectedThemeIdState(id);
    dispatch(setSelectedThemeId(id));
    setSelectedThemePreviewImage(image || block?.logo || null);
    setShowConfirmModal(true);
  };

  const handleConfirmTheme = () => {
    if (selectedThemeId) {
      navigateToCopy();
    }
  };

  const handleReselect = () => {
    setShowConfirmModal(false);
  };

  return (
    <Layout>
      {/* <LanguageSelector /> */} {/* UI 제거 (기능은 유지) */}
      <HomeButton />
      <Timer initialTime={75} onTimeout={handleThemeTimeout} />
      <Container>
        <LogoBox />
        <DashedBox>
          <PaginationButton onClick={goToPrevPage} disabled={currentPage === 1}>
            <ArrowLeftIcon disabled={currentPage === 1} theme={theme} />
          </PaginationButton>
          <ThemeGridContainer>
            {currentThemes.map((theme) => {
              const previewImage = theme.previewImage;

              return (
                <ThemeItem
                  key={theme.id}
                  selected={selectedThemeId === theme.id}
                  onClick={() => handleThemeSelect(theme.id, previewImage)}
                >
                  <img
                    src={theme.themeImage || block?.logo}
                    alt={t('theme.title', { id: theme.id })}
                  />
                </ThemeItem>
              );
            })}
          </ThemeGridContainer>
          <PaginationButton onClick={goToNextPage} disabled={currentPage === totalPages}>
            <ArrowRightIcon disabled={currentPage === totalPages} theme={theme} />
          </PaginationButton>
          <PageStatus>{`${currentPage} / ${totalPages}`}</PageStatus>
        </DashedBox>

        {showConfirmModal && (
          <ThemeConfirmModal>
            <ModalContent>
              <ModalPreviewImage src={selectedThemePreviewImage || ''} alt={t('theme.preview')} />
              <ModalButtonsContainer>
                <ModalButton onClick={handleReselect}>{t('theme.reselect')}</ModalButton>
                <ModalButton primary onClick={handleConfirmTheme}>
                  {t('theme.select')}
                </ModalButton>
              </ModalButtonsContainer>
            </ModalContent>
          </ThemeConfirmModal>
        )}
      </Container>
    </Layout>
  );
}
